import {
  Autocomplete,
  Box,
  CardMedia,
  Icon,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dashedIcon from '../assets/dashed-line.png'
import lineIcon from '../assets/line.png'
import {
  getDepartmentData,
  getEmployeeData,
  getSimilarityScore,
} from 'services/api/modules/recruitment/job-application'
import { useQuery, useQueryClient } from 'react-query'
import defaultAvatar from 'assets/images/default_avatar.png'

const EvaluateTeamFit = ({ evaluateTeamData, userScore }) => {
  const [department, setDepartment] = useState(null)
  const [employee, setEmployee] = useState(null)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [selectedDepartment, setSelectedDepartment] = useState(null)

  // Clear functions
  const clearEmployeeSelection = () => {
    setSelectedEmployee(null)
    setEmployee(null)
  }

  const clearDepartmentSelection = () => {
    setSelectedDepartment(null)
    setDepartment(null)
    setSelectedEmployee(null)
    setEmployee(null)
  }

  useEffect(() => {
    evaluateTeamData({ department, employee })
  }, [department, employee])

  // API CALL
  const queryClient = useQueryClient()
  const {
    isLoading: departmentIsLoading,
    error: departmentError,
    data: departmentData,
  } = useQuery(
    ['getDepartmentData'],
    async () => {
      const { data } = await getDepartmentData()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: employeeIsLoading,
    error: employeeError,
    data: employeeData,
  } = useQuery(
    ['getEmployeeData', department?.id],
    async () => {
      if (department) {
        const { data } = await getEmployeeData(department?.id)
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: similarityScoreIsLoading,
    error: similarityScoreError,
    data: similarityScoreData,
  } = useQuery(
    ['getSimilarityScore', userScore, department?.id, employee?.id],
    async () => {
      const benchmarkScores = [
        userScore?.freedom?.dominant_percentage * 0.01,
        userScore?.adventurous?.dominant_percentage * 0.01,
        userScore?.teamwork?.dominant_percentage * 0.01,
        userScore?.systematic?.dominant_percentage * 0.01,
        userScore?.ambitious?.dominant_percentage * 0.01,
      ]

      const departmentComparison = {
        id: department?.id,
        type: 1,
        score: [
          department?.personality_traits?.freedom?.average_percentage * 0.01,
          department?.personality_traits?.adventurous?.average_percentage * 0.01,
          department?.personality_traits?.teamwork?.average_percentage * 0.01,
          department?.personality_traits?.systematic?.average_percentage * 0.01,
          department?.personality_traits?.ambitious?.average_percentage * 0.01,
        ],
      }

      const comparison = employee
        ? [
            departmentComparison,
            {
              id: employee?.id,
              type: 2,
              score: [
                employee?.personality_traits?.freedom?.dominant_percentage * 0.01,
                employee?.personality_traits?.adventurous?.dominant_percentage * 0.01,
                employee?.personality_traits?.teamwork?.dominant_percentage * 0.01,
                employee?.personality_traits?.systematic?.dominant_percentage * 0.01,
                employee?.personality_traits?.ambitious?.dominant_percentage * 0.01,
              ],
            },
          ]
        : [departmentComparison]

      const { data } = await getSimilarityScore({
        benchmark: benchmarkScores,
        comparison,
      })

      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'start',
        gap: '24px',
      }}
    >
      {similarityScoreIsLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
        >
          <Skeleton variant="text" width={'60%'} />
        </Box>
      ) : (
        <>
          {department && similarityScoreData && similarityScoreData.length > 0 ? (
            <Typography textAlign="center" width="100%" variant="subtitle1">
              {`Similarity score compared to `}
              <span style={{ color: '#D09A01', fontWeight: 600 }}>{department?.name}</span>
              {` : `}
              <Box component="span" fontWeight={600}>
                {`${similarityScoreData[0]?.similarityScore}%`}
              </Box>
              {employee && similarityScoreData.length > 1 ? (
                <>
                  {` , `}
                  <span style={{ color: '#D09A01', fontWeight: 600 }}>{employee?.name}</span>
                  {` : `}
                  <Box component="span" fontWeight={600}>
                    {`${similarityScoreData[1]?.similarityScore}%`}
                  </Box>
                </>
              ) : null}
            </Typography>
          ) : (
            <Typography textAlign="center" fontWeight={600} width="100%" variant="subtitle1">
              Select department and employee to compare
            </Typography>
          )}
        </>
      )}

      <Box width="100%" border={1} borderColor="gray-line.main" borderRadius="4.5px">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          borderBottom={1}
          borderColor="gray-line.main"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingX: '12px',
            }}
            width="56px"
          >
            <hr style={{ all: 'unset', width: 50, border: '1px solid', borderColor: '#2666BE' }} />
          </Box>
          <Box
            borderRight={1}
            borderLeft={1}
            borderColor="gray-line.main"
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingY="28px"
            width="117px"
            paddingX="24px"
          >
            <Typography>Department</Typography>
          </Box>
          <Autocomplete
            value={selectedDepartment}
            onChange={(e, value) => {
              setSelectedDepartment(value)
              setDepartment(value)
            }}
            popupIcon={<ExpandMoreIcon />}
            options={departmentData || []}
            getOptionLabel={(option) => option.name || ''}
            sx={{
              '& .MuiInputBase-root': {
                outline: 'none',
                bgcolor: 'none',
                py: 2,
                marginY: 0,
                // outlineColor: 'red !important',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.25,
                  py: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 0,
                },

                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiAutocomplete-option': {
                    backgroundColor: 'gray-background.main',
                  },
                  border: 0,
                },
              },

              height: '100%',
              width: '100%',
            }}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option[aria-selected=true]': {
                  backgroundColor: 'transparent',
                  '&.Mui-focused': {
                    backgroundColor: 'gray-background.main',
                  },
                },
                '& .MuiAutocomplete-option[aria-selected=false]': {
                  backgroundColor: 'transparent',
                  '&.Mui-focused': {
                    backgroundColor: 'gray-background.main',
                  },
                },
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '4.5px',
                  backgroundColor: 'rgba(206,208,212)',
                },
              },
            }}
            renderInput={(params) => (
              <>
                {selectedDepartment ? (
                  <Box display="flex" alignItems="center" gap="16px" padding="16px">
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '100%',
                        border: 1,
                        borderColor: 'gray-line.main',
                        padding: 1,
                        backgroundColor: 'gray-background.main',
                      }}
                    >
                      <Icon sx={{ color: 'alternate.main' }}>groups</Icon>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" color="dark-text.main">
                        {selectedDepartment?.name}
                      </Typography>

                      <Typography variant="body2" color="light-gray-text.main">
                        {selectedDepartment?.staff_count} members
                      </Typography>
                    </Box>
                    <Icon
                      onClick={clearDepartmentSelection}
                      sx={{ color: 'error.main', cursor: 'pointer', fontSize: '21px' }}
                    >
                      clear
                    </Icon>
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      {...params}
                      placeholder="Search or select"
                      InputLabelProps={{
                        sx: {
                          display: 'none',
                        },
                      }}
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  </Box>
                )}
              </>
            )}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            noOptionsText="No departments available"
            renderOption={(props, option) => (
              <Box {...props} display="flex" alignItems="center">
                <Typography padding="12px" variant="subtitle1" color="dark-text.main">
                  {option?.name}
                </Typography>
              </Box>
            )}
            PaperComponent={({ children }) => <Paper elevation={2}>{children}</Paper>}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingX: '12px',
            }}
            width="56px"
          >
            <hr style={{ all: 'unset', width: 50, border: '1px dashed', borderColor: '#2666BE' }} />
          </Box>
          <Box
            borderRight={1}
            borderLeft={1}
            borderColor="gray-line.main"
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingY="28px"
            width="117px"
            paddingX="24px"
          >
            <Typography>Employee</Typography>
          </Box>
          <Autocomplete
            value={selectedEmployee}
            disabled={department === null ?? false}
            onChange={(e, value) => {
              setSelectedEmployee(value)
              setEmployee(value)
            }}
            popupIcon={<ExpandMoreIcon />}
            options={employeeData || []}
            getOptionLabel={(option) => option?.name || ''}
            sx={{
              '& .MuiInputBase-root': {
                outline: 'none',
                bgcolor: 'none',
                py: 2,
                marginY: 0,
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                  marginY: 0,
                  py: 2,
                },
                // outlineColor: 'red !important',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.25,
                  py: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 0,
                },

                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiAutocomplete-option': {
                    backgroundColor: 'gray-background.main',
                  },
                  border: 0,
                },
              },

              height: '100%',
              width: '100%',
            }}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option[aria-selected=true]': {
                  backgroundColor: 'transparent',
                  '&.Mui-focused': {
                    backgroundColor: 'gray-background.main',
                  },
                },
                '& .MuiAutocomplete-option[aria-selected=false]': {
                  backgroundColor: 'transparent',
                  '&.Mui-focused': {
                    backgroundColor: 'gray-background.main',
                  },
                },
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '4.5px',
                  backgroundColor: 'rgba(206,208,212)',
                },
              },
            }}
            renderInput={(params) => (
              <>
                {selectedEmployee ? (
                  <Box display="flex" alignItems="center" gap="16px" padding="16px">
                    <CardMedia
                      component="img"
                      sx={{
                        height: 45,
                        width: 45,
                        objectFit: 'cover',
                        borderRadius: '100%',
                      }}
                      src={selectedEmployee?.profile_image_url ?? defaultAvatar}
                    />
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" color="dark-text.main">
                        {selectedEmployee?.name}
                      </Typography>

                      <Typography variant="body2" color="light-gray-text.main">
                        {selectedEmployee?.email}
                      </Typography>
                    </Box>
                    <Icon
                      onClick={clearEmployeeSelection}
                      sx={{ color: 'error.main', cursor: 'pointer', fontSize: '21px' }}
                    >
                      clear
                    </Icon>
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      {...params}
                      placeholder={department ? 'Search or select' : 'Select department first'}
                      InputLabelProps={{
                        sx: {
                          display: 'none',
                        },
                      }}
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  </Box>
                )}
              </>
            )}
            PaperComponent={({ children }) => <Paper elevation={2}>{children}</Paper>}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            getOptionDisabled={(option) => option.personality_traits === null}
            noOptionsText="No employees available"
            renderOption={(props, option) => (
              <Box {...props} display="flex" alignItems="center" gap="16px" padding="16px">
                <CardMedia
                  component="img"
                  sx={{
                    height: 45,
                    width: 45,
                    objectFit: 'cover',
                    borderRadius: '100%',
                  }}
                  src={option?.profile_image_url ?? defaultAvatar}
                />
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" color="dark-text.main">
                    {option?.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color={option.personality_traits ? 'light-gray-text.main' : 'error.main'}
                  >
                    {option?.email} {option.personality_traits ? null : `(no data)`}
                    {option.personality_traits === null && (
                      <Icon sx={{ color: 'error.main', fontSize: 16, verticalAlign: 'middle' }}>
                        error
                      </Icon>
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Box>
    </Box>
  )
}
export default EvaluateTeamFit
